import { useEffect, useState } from 'react';
import { Loader2, FolderPlus, Edit3, X } from 'lucide-react';
import type { ImapFolderNode } from '../../lib/api';

interface FolderCreateModalProps {
  isOpen: boolean;
  /** Tryb: 'create' tworzy nowy folder, 'rename' zmienia nazwę istniejącego. */
  mode: 'create' | 'rename';
  /** Aktualny path (rename). */
  initialPath?: string;
  /** Opis konta IMAP (wyświetlany w nagłówku). */
  accountName?: string;
  /** Folder rodzic (create-only) — gdy ustawiony, modal bierze tylko nazwę childa
   *  i konstruuje pełną ścieżkę przez {parentFolder.path}{delimiter}{childName}. */
  parentFolder?: ImapFolderNode | null;
  /** Placeholder dla inputu — np. "INBOX/Klienci/2026". */
  placeholder?: string;
  onSubmit: (path: string) => Promise<void>;
  onClose: () => void;
}

/**
 * Modal do tworzenia folderu lub zmiany nazwy. Operacja idzie na serwer IMAP
 * (mailboxCreate / mailboxRename) — wyświetlamy ostrzeżenie że wpływa na webmail.
 * Zamyka się na Escape i kliknięciu w backdrop.
 */
export function FolderCreateModal({
  isOpen,
  mode,
  initialPath = '',
  accountName,
  parentFolder = null,
  placeholder,
  onSubmit,
  onClose,
}: FolderCreateModalProps) {
  // W trybie subfolderu zaczynamy z pustym inputem (bierzemy tylko nazwę childa).
  const [path, setPath] = useState(parentFolder ? '' : initialPath);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setPath(parentFolder ? '' : initialPath);
      setError(null);
    }
  }, [isOpen, initialPath, parentFolder]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, submitting, onClose]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    const trimmed = path.trim();
    if (!trimmed) {
      setError('Nazwa folderu jest wymagana');
      return;
    }
    // Subfolder: konstruuj pełną ścieżkę z delimiterem rodzica (np. "." dla home.pl).
    const fullPath = parentFolder
      ? `${parentFolder.path}${parentFolder.delimiter || '/'}${trimmed}`
      : trimmed;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(fullPath);
      onClose();
    } catch (err) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setError(axiosErr?.response?.data?.error || 'Nie udało się wykonać operacji na serwerze IMAP');
    } finally {
      setSubmitting(false);
    }
  };

  const title = mode === 'create' ? 'Nowy folder IMAP' : 'Zmień nazwę folderu';
  const confirmLabel = mode === 'create' ? 'Utwórz folder' : 'Zapisz zmianę';
  const Icon = mode === 'create' ? FolderPlus : Edit3;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={() => !submitting && onClose()}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-surface-raised border border-border shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-border flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-accent-primary/10 flex items-center justify-center shrink-0">
            <Icon className="w-5 h-5 text-accent-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-text-primary">{title}</h3>
            {accountName && (
              <p className="text-xs text-text-secondary mt-0.5 truncate">Konto: {accountName}</p>
            )}
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            className="btn-ghost p-1"
            aria-label="Zamknij"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          {parentFolder && (
            <div className="text-xs text-text-secondary bg-surface-hover/40 rounded-md px-3 py-2 border border-border-subtle">
              Folder nadrzędny: <span className="text-text-primary">{parentFolder.path}</span>
            </div>
          )}
          <label className="block">
            <span className="text-xs text-text-secondary mb-1 block">
              {parentFolder ? 'Nazwa nowego folderu' : 'Ścieżka folderu IMAP'}
            </span>
            <input
              type="text"
              value={path}
              onChange={(e) => setPath(e.target.value)}
              placeholder={placeholder || (parentFolder ? 'np. Klienci2026' : 'INBOX/Klienci/2026')}
              autoFocus
              className="input w-full text-sm"
              disabled={submitting}
            />
            {!parentFolder && (
              <span className="text-[10px] text-text-muted mt-1 block">
                Użyj separatora „/” żeby utworzyć strukturę zagnieżdżoną.
              </span>
            )}
          </label>
          <div className="bg-warning-bg border border-warning-border rounded-lg p-3 text-[11px] text-warning">
            Operacja wykonuje się bezpośrednio na serwerze IMAP — zmiana będzie widoczna
            również w Twoim webmailu (Gmail/Outlook/itd.).
          </div>
          {error && (
            <div className="text-xs text-danger">{error}</div>
          )}
        </div>
        <div className="p-4 border-t border-border flex items-center gap-2">
          <button
            onClick={handleSubmit}
            disabled={submitting || !path.trim()}
            className="btn-primary text-sm flex items-center gap-1.5"
          >
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />}
            {confirmLabel}
          </button>
          <button
            onClick={onClose}
            disabled={submitting}
            className="btn-secondary text-sm ml-auto"
          >
            Anuluj
          </button>
        </div>
      </div>
    </div>
  );
}
